export const UPGRADE_MILESTONE_INTERVAL = 100;
export const UPGRADE_MILESTONE_MULTIPLIER = 2;

export interface UpgradeMilestoneOptions {
  readonly interval?: number;
  readonly multiplier?: number;
}

function normalizeUpgradeCount(upgradeCount: number): number {
  if (!Number.isFinite(upgradeCount) || upgradeCount <= 0) return 0;
  return Math.floor(upgradeCount);
}

function normalizeInterval(interval: number): number {
  if (!Number.isFinite(interval) || interval <= 0) {
    return UPGRADE_MILESTONE_INTERVAL;
  }
  const normalized = Math.floor(interval);
  return normalized > 0 ? normalized : UPGRADE_MILESTONE_INTERVAL;
}

function normalizeMultiplier(multiplier: number): number {
  if (!Number.isFinite(multiplier) || multiplier < 1) {
    return UPGRADE_MILESTONE_MULTIPLIER;
  }
  return multiplier;
}

export function calculateUpgradeMilestoneCount(
  upgradeCount: number,
  options: Pick<UpgradeMilestoneOptions, "interval"> = {},
): number {
  const interval = normalizeInterval(
    options.interval ?? UPGRADE_MILESTONE_INTERVAL,
  );
  return Math.floor(normalizeUpgradeCount(upgradeCount) / interval);
}

export function calculateUpgradeMilestoneMultiplier(
  upgradeCount: number,
  options: UpgradeMilestoneOptions = {},
): number {
  const milestoneCount = calculateUpgradeMilestoneCount(upgradeCount, options);
  const multiplier = normalizeMultiplier(
    options.multiplier ?? UPGRADE_MILESTONE_MULTIPLIER,
  );
  const value = Math.pow(multiplier, milestoneCount);
  return Number.isFinite(value) ? value : Number.MAX_VALUE;
}

export function calculateUpgradeMilestoneRemaining(
  upgradeCount: number,
  options: Pick<UpgradeMilestoneOptions, "interval"> = {},
): number {
  const interval = normalizeInterval(
    options.interval ?? UPGRADE_MILESTONE_INTERVAL,
  );
  const count = normalizeUpgradeCount(upgradeCount);
  const remainder = count % interval;
  return remainder === 0 ? interval : interval - remainder;
}

export function isUpgradeMilestonePurchase(
  currentEnhancementLevel: number,
  options: Pick<UpgradeMilestoneOptions, "interval"> = {},
): boolean {
  return (
    calculateUpgradeMilestoneRemaining(currentEnhancementLevel, options) === 1
  );
}
